import {
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Dropdown,
  MenuProps,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';

import {
  createUserAsAdmin,
  deleteUserAsAdmin,
  fetchUsersAsAdmin,
  resendPasswordResetEmailAsAdmin,
  resendVerificationEmailAsAdmin,
  updateUserAsAdmin,
} from '@/apis';
import { UserModal } from '@/components/Admin/Modal';
import PageHeader from '@/components/Admin/PageHeader';
import StateRenderer from '@/components/Admin/StateRenderer';
import TableContainer from '@/components/Admin/TableContainer';
import { MessageType } from '@/enums';
import { useApiRequest, useAppFeedback } from '@/hooks';
import {
  AdminUser,
  CreateUserAsAdminRequest,
  UpdateUserAsAdminRequest,
} from '@/types/apis/admin';
import { formatDateTime } from '@/utils/dateFormatter';

const { Text } = Typography;

interface AdminUserWithPassword extends Omit<
  AdminUser,
  'createdAt' | 'updatedAt'
> {
  password: string;
}

const AdminUsersPage = () => {
  const { showMessage, showConfirm } = useAppFeedback();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isUserModalOpen, setUserModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const pageSize = 10;

  // Use useApiRequest for user list fetching
  const {
    loading,
    error: fetchError,
    execute: loadUsers,
  } = useApiRequest(
    async () => {
      const response = await fetchUsersAsAdmin(page, pageSize);
      setUsers(response.data.items);
      setTotal(response.data.total);
    },
    {
      requestKey: `admin-users-${page}`,
      onError: (err) => {
        const errorMessage = '載入用戶失敗，請稍後再試';
        showMessage(errorMessage, MessageType.Error);
        console.error('Error loading users:', err);
      },
    },
  );

  const error = fetchError ? '載入用戶失敗，請稍後再試' : null;

  useEffect(() => {
    loadUsers();
  }, [page]);

  const handleSaveUser = async (data: AdminUser | AdminUserWithPassword) => {
    try {
      if (modalMode === 'edit' && 'id' in data) {
        // Edit mode
        const updateData: UpdateUserAsAdminRequest = {
          name: data.name,
          email: data.email,
          isVerified: data.isVerified,
          ...(data.isAdmin !== undefined ? { isAdmin: data.isAdmin } : {}),
          provider: data.provider,
        };
        await updateUserAsAdmin(data.id, updateData);
        showMessage('用戶更新成功', MessageType.Success);
      } else {
        // Add mode
        const createData: CreateUserAsAdminRequest = {
          name: data.name,
          email: data.email,
          password: (data as AdminUserWithPassword).password,
          ...(data.isAdmin !== undefined ? { isAdmin: data.isAdmin } : {}),
          provider: data.provider,
        };
        await createUserAsAdmin(createData);
        showMessage('用戶新增成功', MessageType.Success);
      }
      setUserModalOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      const errorMessage =
        modalMode === 'edit'
          ? '用戶更新失敗，請稍後再試'
          : '用戶新增失敗，請稍後再試';
      showMessage(errorMessage, MessageType.Error);
      console.error(
        `Error ${modalMode === 'edit' ? 'updating' : 'creating'} user:`,
        err,
      );
    }
  };

  const handleAddUser = () => {
    setModalMode('add');
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleEditUser = (user: AdminUser) => {
    setModalMode('edit');
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const userColumns: ColumnsType<AdminUser> = [
    {
      title: '用戶',
      dataIndex: 'user',
      key: 'user',
      render: (_, user) => (
        <Space>
          <Avatar
            src={user.avatarUrl}
            size="small"
            style={{ backgroundColor: 'var(--ant-color-primary)' }}
          >
            {user.name.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div className="font-medium">{user.name}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {user.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: '驗證狀態',
      dataIndex: 'isVerified',
      key: 'verified',
      width: 120,
      render: (isVerified: boolean) => (
        <Tag color={isVerified ? 'green' : 'orange'}>
          {isVerified ? '已驗證' : '未驗證'}
        </Tag>
      ),
    },
    {
      title: '註冊方式',
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (provider: string) => (
        <Tag color={provider === 'local' ? 'blue' : 'red'}>
          {provider === 'local' ? '本地' : provider.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (createdAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDateTime(new Date(createdAt))}
        </Text>
      ),
    },
    {
      title: '更新時間',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (updatedAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDateTime(new Date(updatedAt))}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, user) => {
        const emailMenuItems: MenuProps['items'] = [
          {
            key: 'verification',
            label: '重寄驗證郵件',
            icon: <MailOutlined />,
            disabled: user.isVerified,
            onClick: () => handleResendVerificationEmail(user.id, user.name),
          },
          {
            key: 'password-reset',
            label: '重寄密碼重設郵件',
            icon: <MailOutlined />,
            disabled: !user.hasPendingPasswordReset,
            onClick: () => handleResendPasswordResetEmail(user.id, user.name),
          },
        ];

        return (
          <Space size="small">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(user)}
              title="編輯用戶"
              size="small"
            />
            <Dropdown
              menu={{ items: emailMenuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button type="text" size="small" icon={<MailOutlined />} />
            </Dropdown>
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteConfirm(user.id, user.name)}
              loading={deletingUser === user.id}
              title="刪除用戶"
              danger
              size="small"
            />
          </Space>
        );
      },
    },
  ];

  const handleDeleteConfirm = (userId: string, userName: string) => {
    showConfirm(
      '確認刪除',
      `確定要刪除用戶「${userName}」嗎？此操作無法復原。`,
      async () => {
        try {
          setDeletingUser(userId);
          await deleteUserAsAdmin(userId);
          await loadUsers();
          showMessage('用戶刪除成功', MessageType.Success);
        } catch (err) {
          showMessage('用戶刪除失敗，請稍後再試', MessageType.Error);
          console.error('Error deleting user:', err);
        } finally {
          setDeletingUser(null);
        }
      },
      undefined,
      '確定刪除',
      '取消',
      'danger',
    );
  };

  const handleResendVerificationEmail = async (
    userId: string,
    userName: string,
  ) => {
    showConfirm(
      '確認重新發送驗證郵件',
      `確定要重新發送驗證郵件給用戶「${userName}」嗎？`,
      async () => {
        try {
          await resendVerificationEmailAsAdmin(userId);
          showMessage('驗證郵件已成功發送', MessageType.Success);
        } catch (err) {
          const errorMessage =
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || '驗證郵件發送失敗，請稍後再試';
          showMessage(errorMessage, MessageType.Error);
          console.error('Error resending verification email:', err);
        }
      },
      undefined,
      '確定發送',
      '取消',
    );
  };

  const handleResendPasswordResetEmail = async (
    userId: string,
    userName: string,
  ) => {
    showConfirm(
      '確認重新發送密碼重設郵件',
      `確定要重新發送密碼重設郵件給用戶「${userName}」嗎？`,
      async () => {
        try {
          await resendPasswordResetEmailAsAdmin(userId);
          showMessage('密碼重設郵件已成功發送', MessageType.Success);
        } catch (err) {
          const errorMessage =
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || '密碼重設郵件發送失敗，請稍後再試';
          showMessage(errorMessage, MessageType.Error);
          console.error('Error resending password reset email:', err);
        }
      },
      undefined,
      '確定發送',
      '取消',
    );
  };

  return (
    <>
      <PageHeader
        title="用戶管理"
        description="管理系統中的所有用戶帳戶和權限設置。"
      />

      <TableContainer
        title="用戶列表"
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddUser}
          >
            新增用戶
          </Button>
        }
      >
        <StateRenderer
          loading={loading}
          error={error}
          empty={users.length === 0}
          emptyMessage="系統中尚未有用戶註冊"
          emptyIcon={<UserOutlined />}
          onRetry={loadUsers}
        >
          <Table
            scroll={{ x: 'max-content' }}
            columns={userColumns}
            dataSource={users}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              onChange: setPage,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`,
            }}
            size="small"
            loading={loading}
          />
        </StateRenderer>
      </TableContainer>

      <UserModal
        isOpen={isUserModalOpen}
        user={selectedUser}
        mode={modalMode}
        onClose={() => {
          setUserModalOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
      />
    </>
  );
};

export default AdminUsersPage;
